import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { type AuthSession, type LoginPayload } from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = environment.baseUrl;
  private readonly storageKey = 'erg_auth_session';
  private readonly sessionState = signal<AuthSession | null>(this.readStoredSession());

  readonly session = this.sessionState.asReadonly();

  constructor(private readonly http: HttpClient) {}

  login(payload: LoginPayload, remember = false): Observable<AuthSession> {
    return this.http.post<AuthSession>(`${this.baseUrl}/Auth/Login`, payload).pipe(
      tap((session) => {
        if (!session.authenticated) {
          throw new Error('Authentication failed.');
        }

        this.persistSession(session, remember);
      })
    );
  }

  logout(): void {
    this.sessionState.set(null);
    localStorage.removeItem(this.storageKey);
    sessionStorage.removeItem(this.storageKey);
  }

  isAuthenticated(): boolean {
    return Boolean(this.sessionState()?.authenticated);
  }

  isAdmin(): boolean {
    return this.sessionState()?.role?.toUpperCase() === 'ADMIN';
  }

  private persistSession(session: AuthSession, remember: boolean): void {
    this.sessionState.set(session);

    const serialized = JSON.stringify(session);
    const targetStorage = remember ? localStorage : sessionStorage;
    const otherStorage = remember ? sessionStorage : localStorage;

    targetStorage.setItem(this.storageKey, serialized);
    otherStorage.removeItem(this.storageKey);
  }

  private readStoredSession(): AuthSession | null {
    const stored = localStorage.getItem(this.storageKey) ?? sessionStorage.getItem(this.storageKey);

    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored) as AuthSession;
    } catch {
      localStorage.removeItem(this.storageKey);
      sessionStorage.removeItem(this.storageKey);
      return null;
    }
  }
}
